/**
 * Form Configuration Service
 * Manages dynamic form field configurations for registration and contact forms
 */

// Default form configurations (fallback when API is not available)
export const defaultFormConfigs = {
  registration_pet_owner: {
    steps: [
      {
        stepNumber: 1,
        stepTitle: "I am a",
        fields: [
          { name: "user_roles", label: "Select", type: "select", required: true, order: 1, options: [
            { value: "pet_owner", label: "Pet Owner" },
            { value: "foster_parent", label: "Foster Parent" },
            { value: "rescuer", label: "Rescuer" },
            { value: "looking_to_adopt", label: "Looking To Adopt" },
            { value: "looking_for_services", label: "Looking For Services" },
            { value: "volunteer", label: "Volunteer" },
            { value: "Others", label: "Others" },
          ]}
        ]
      },
      {
        stepNumber: 2,
        stepTitle: "Basic Information",
        fields: [
          { name: "full_name", label: "Full Name", type: "text", placeholder: "Enter your full name", required: true, order: 1 },
          { name: "email", label: "Email ID", type: "email", placeholder: "example@domain.com", required: true, order: 2 },
          { name: "phone", label: "Mobile No.", type: "tel", placeholder: "Enter 10-digit mobile number", required: true, order: 3 },
          { name: "password", label: "Password", type: "password", placeholder: "Create a strong password", required: true, order: 4 },
          { name: "confirm_password", label: "Confirm Password", type: "password", placeholder: "Repeat your password", required: true, order: 5 },
          { name: "city", label: "City", type: "text", placeholder: "e.g. Hyderabad", required: true, order: 6 },
          { name: "area_locality", label: "Area / Locality", type: "text", placeholder: "e.g. Bandra West", required: true, order: 7 },
          { name: "pincode", label: "Pincode", type: "text", placeholder: "e.g. 400050", required: true, order: 8 },
        ]
      },
      
      {
        stepNumber: 3,
        stepTitle: "Add Your Pet",
        fields: [
          { name: "pet_name", label: "Pet Name", type: "text", placeholder: "What do you call them?", required: false, order: 1 },
          { name: "pet_type", label: "Pet Type", type: "select", required: false, order: 2, options: [
            { value: "dog", label: "Dog" },
            { value: "cat", label: "Cat" },
            { value: "bird", label: "Bird" },
            { value: "fish", label: "Fish" },
            { value: "rabbit", label: "Rabbit" },
            { value: "other", label: "Other" },
          ]},
          { 
            name: "breed", 
            label: "Breed", 
            type: "select", 
            required: false, 
            order: 3,
            dynamicOptions: {
              field: "pet_type",
              defaultOption: "Select breed",
              options: {
                dog: [
                  { value: "indie", label: "Indie" },
                  { value: "labrador", label: "Labrador" },
                  { value: "golden_retriever", label: "Golden Retriever" },
                  { value: "shih_tzu", label: "Shih Tzu" },
                  { value: "german_shephard", label: "German Shephard/Alsation" },
                  { value: "beagle", label: "Beagle" },
                  { value: "husky", label: "Husky" },
                  { value: "rottweiler", label: "Rottweiler" },
                  { value: "pug", label: "Pug" },
                  { value: "cocker_spaniel", label: "Cocker Spaniel" },
                  { value: "indian_spitz", label: "Indian Spitz" },
                  { value: "pomeranian", label: "Pomeranian" },
                  { value: "doberman", label: "Doberman" },
                  { value: "rajapalayam", label: "Rajapalayam" },
                  { value: "chippiparai", label: "Chippiparai" },
                  { value: "kanni", label: "Kanni" },
                  { value: "st_bernard", label: "St Bernard" },
                  { value: "chow_chow", label: "Chow Chow" },
                  { value: "mastiff", label: "Mastiff" },
                  { value: "poodle", label: "Poodle" },
                  { value: "boxer", label: "Boxer" },
                  { value: "great_dane", label: "Great Dane" },
                  { value: "dachshund", label: "Dachshund" },
                  { value: "others", label: "Others" },
                ],
                cat: [
                  { value: "persian", label: "Persian" },
                  { value: "siamese", label: "Siamese" },
                  { value: "indian", label: "Indian" },
                  { value: "maine_coon", label: "Maine Coon" },
                  { value: "others", label: "Others" },
                ]
              }
            }
          },
          { 
            name: "other_breed", 
            label: "Please specify breed", 
            type: "text", 
            placeholder: "Enter breed name", 
            required: false, 
            order: 4,
            condition: {
              field: "breed",
              value: "others"
            }
          },
          { name: "age", label: "Age", type: "text", placeholder: "e.g. 2 years / 4 months", required: false, order: 5 },
          { name: "gender", label: "Gender", type: "select", required: false, order: 6, options: [
            { value: "male", label: "Male" },
            { value: "female", label: "Female" },
          ]},
          { name: "vaccinated", label: "Vaccinated?", type: "select", required: false, order: 7, options: [
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" },
          ]},
          { 
            name: "vaccination_certificate", 
            label: "Vaccination Certificate", 
            type: "file", 
            required: false, 
            order: 8,
            condition: {
              field: "vaccinated",
              value: "yes"
            }
          },
          { name: "special_needs", label: "Special Needs / Allergies", type: "textarea", placeholder: "Mention any specific medical conditions, allergies, or diets (Optional)", required: false, order: 9 },
        ]
      },
      {
        stepNumber: 4,
        stepTitle: "Preferred Services",
        fields: [
          { name: "preferred_services", label: "Select services you're interested in", type: "checkbox", required: false, order: 1, options: [
            { value: "grooming", label: "✂️ Grooming" },
            { value: "walking", label: "🚶 Walking" },
            { value: "training", label: "🎓 Training" },
            { value: "vet_services", label: "🩺 Vet Services" },
            { value: "pet_sitting", label: "🐕 Pet Sitting" },
            { value: "adoption", label: "🏡 Adoption" },
            { value: "food_accessories", label: "🛒 Food & Accessories" },
            { value: "pet_events", label: "🎉 Pet Events" },
          ]}
        ]
      },
      {
        stepNumber: 5,
        stepTitle: "Food Preference",
        fields: [
          {
            name: "food_preference",
            label: "Food Preference (Select up to 3)",
            type: "checkbox",
            required: false,
            order: 1,
            maxSelections: 3,
            options: [
              { value: "pedigree", label: "Pedigree" },
              { value: "royal_canin", label: "Royal Canin" },
              { value: "whiskas", label: "Whiskas" },
              { value: "farmina_nd", label: "Farmina N&D" },
              { value: "chappi", label: "Chappi" },
              { value: "drools", label: "Drools" },
              { value: "purepet", label: "Purepet" },
              { value: "me_o", label: "Me-O" },
              { value: "veg_home_food", label: "Veg Home Food" },
              { value: "non_veg_home_food", label: "Non Veg Home Food" },
              { value: "others", label: "Others" },
            ]
          },
          { 
            name: "other_food_preference", 
            label: "Please specify other food preference", 
            type: "text", 
            placeholder: "Enter food preference", 
            required: false, 
            order: 2,
            condition: {
              field: "food_preference",
              value: "others",
              conditionType: "includes"
            }
          },
        ]
      },
      {
        stepNumber: 6,
        stepTitle: "Terms & Conditions",
        
        fields: [
          {
            name: "terms_accepted",
            label: "I have read and understood the above Terms & Conditions",
            type: "checkbox",
            required: true,
            order: 1,
            popupTitle: "Terms & Conditions",
            popupPdfUrl: "/assets/docs/TermsNConditions.pdf"
          },
          {
            name: "community_guidelines",
            label: "I have read and understood the above Community Guidelines",
            type: "checkbox",
            required: true,
            order: 2,
            popupTitle: "Community Guidelines",
            popupPdfUrl: "/assets/docs/EverythingPawzibleCommunityGuidelines.pdf"
          },
          { name: "location_access", label: "Allow Location Access", type: "checkbox", required: false, order: 3 },
        ]
      }
    ]
  },
  registration_provider: {
    steps: [
      
      {
        stepNumber: 1,
        stepTitle: "Service Category",
        fields: [
          { name: "service_categories", label: "Select services you offer", type: "checkbox", required: true, order: 1, options: [
            { value: "veterinary", label: "🏥 Veterinary Clinic" },
            { value: "grooming", label: "✂️ Grooming" },
            { value: "training", label: "🎓 Training" },
            { value: "walking", label: "🚶 Pet Walking" },
            { value: "boarding", label: "🏡 Boarding" },
            { value: "ngo", label: "🤝 NGO" },
          ]}
        ]
      },
      {
        stepNumber: 2,
        stepTitle: "Basic Information",
        fields: [
          { name: "full_name", label: "Full Name", type: "text", placeholder: "Enter your full name", required: true, order: 1 },
          { name: "business_name", label: "Business Name", type: "text", placeholder: "Enter registered business/clinic name", required: true, order: 2 },
          { name: "email", label: "Email ID", type: "email", placeholder: "business@domain.com", required: true, order: 3 },
          { name: "phone", label: "Mobile No.", type: "tel", placeholder: "Enter business phone number", required: true, order: 4 },
          { name: "password", label: "Password", type: "password", placeholder: "Create a password", required: true, order: 5 },
          { name: "confirm_password", label: "Confirm Password", type: "password", placeholder: "Repeat your password", required: true, order: 6 },
        ]
      },
      {
        stepNumber: 3,
        stepTitle: "Business Details",
        fields: [
          { name: "business_type", label: "Business Type", type: "select", required: true, order: 1, options: [
            { value: "individual", label: "Individual" },
            { value: "company", label: "Company" },
            { value: "ngo", label: "NGO" },
          ]},
          { name: "years_of_experience", label: "Years Of Experience", type: "number", placeholder: "e.g. 5", required: false, order: 2 },
          { name: "website", label: "Website", type: "text", placeholder: "https://www.yourwebsite.com", required: false, order: 3 },
          { name: "gst_number", label: "GST Number", type: "text", placeholder: "Enter 15-digit GSTIN (Optional)", required: false, order: 4 },
          { name: "business_description", label: "Business Description", type: "textarea", placeholder: "Briefly describe your services, facilities, specialties, etc.", required: false, order: 5 },
        ]
      },
      {
        stepNumber: 4,
        stepTitle: "Location",
        fields: [
          { name: "city", label: "City", type: "text", placeholder: "e.g. Mumbai", required: true, order: 1 },
          { name: "pincode", label: "Pincode", type: "text", placeholder: "e.g. 400050", required: true, order: 2 },
          { name: "address", label: "Business Address", type: "textarea", placeholder: "Complete address of your center or primary operation base", required: true, order: 3 },
        ]
      },
      {
        stepNumber: 5,
        stepTitle: "Availability",
        fields: [
          { name: "availability", label: "Select your availability options", type: "checkbox", required: true, order: 1, options: [
            { value: "home_service", label: "🏠 Home Service" },
            { value: "store_visit", label: "🏢 Store Visit" },
            { value: "online_consultation", label: "💻 Online Consultation" },
            { value: "appointment_only", label: "📅 Appointment Only" },
          ]}
        ]
      },
      {
        stepNumber: 6,
        stepTitle: "Documents",
        fields: [
          { name: "business_logo", label: "Business Logo", type: "file", placeholder: "Upload brand/business logo", required: false, order: 1 },
          { name: "business_license", label: "Business License", type: "file", placeholder: "Upload government registration/license copy", required: false, order: 2 },
          { name: "certificates", label: "Certificates", type: "file", placeholder: "Upload professional qualifications or awards", required: false, order: 3 },
        ]
      },
      {
        stepNumber: 7,
        stepTitle: "Terms & Conditions",
        fields: [
          { name: "terms_accepted", label: "I agree to Terms & Conditions", type: "checkbox", required: true, order: 1 },
          { name: "privacy_accepted", label: "I agree to Privacy Policy", type: "checkbox", required: true, order: 2 },
          { name: "notifications_enabled", label: "Receive Notifications & Updates", type: "checkbox", required: false, order: 3 },
        ]
      }
    ]
  },
  contact_us: {
    steps: [
      {
        stepNumber: 1,
        stepTitle: "Contact Information",
        fields: [
          { name: "name", label: "Name", type: "text", placeholder: "Your full name", required: true, order: 1 },
          { name: "email", label: "Email", type: "email", placeholder: "your@email.com", required: true, order: 2 },
          { name: "phone", label: "Contact No.", type: "tel", placeholder: "Your contact number", required: false, order: 3 },
          { name: "city", label: "City", type: "text", placeholder: "Your city", required: false, order: 7 },
          { name: "pincode", label: "Pincode", type: "text", placeholder: "Your pincode", required: false, order: 8 },
          { name: "message", label: "Message", type: "textarea", placeholder: "How can we help you?", required: true, order: 9 },
        ]
      }
    ]
  },
  // Service-specific registration - starts with service selection, then personal details
  registration_provider_with_service: {
    steps: [
      {
        stepNumber: 1,
        stepTitle: "Service Selection",
        fields: [
          { name: "service_categories", label: "Select your service", type: "radio", required: true, order: 1, options: [
            { value: "training", label: "🎓 Training" },
            { value: "grooming", label: "✂️ Grooming" },
            { value: "veterinary", label: "🏥 Veterinary Clinic" },
            { value: "walking", label: "🚶 Pet Walking" },
            { value: "boarding", label: "🏡 Boarding" },
            { value: "fostering", label: "🤝 Fostering" },
            { value: "pet_food", label: "🛒 Pet Food & Accessories" },
            { value: "pet_events", label: "🎉 Pet Events" },
          ]}
        ]
      },
      {
        stepNumber: 2,
        stepTitle: "Personal Details",
        fields: [
          { name: "full_name", label: "Full Name", type: "text", placeholder: "Enter your full name", required: true, order: 1 },
          { name: "email", label: "Email ID", type: "email", placeholder: "example@domain.com", required: true, order: 2 },
          { name: "phone", label: "Mobile No.", type: "tel", placeholder: "Enter 10-digit mobile number", required: true, order: 3 },
          { name: "password", label: "Password", type: "password", placeholder: "Create a password", required: true, order: 4 },
          { name: "confirm_password", label: "Confirm Password", type: "password", placeholder: "Repeat your password", required: true, order: 5 },
        ]
      },
      
      {
        stepNumber: 3,
        stepTitle: "Location",
        fields: [
          { name: "city", label: "City", type: "text", placeholder: "e.g. Mumbai", required: true, order: 1 },
          { name: "pincode", label: "Pincode", type: "text", placeholder: "e.g. 400050", required: true, order: 2 },
          { name: "address", label: " Address", type: "textarea", placeholder: "Complete address ", required: true, order: 3 },
        ]
      },
      {
        stepNumber: 4,
        stepTitle: "Terms & Conditions",
        fields: [
          {
            name: "terms_accepted",
            label: "I have read and understood the above Terms & Conditions",
            type: "checkbox",
            required: true,
            order: 1,
            popupTitle: "Terms & Conditions",
            popupContent: `
              <div className="terms-pdf-container">
                <p className="text-center mb-3">Please read the Terms & Conditions document below. Scroll to the bottom to acknowledge.</p>
                <iframe
                  src="/assets/docs/TermsNConditions.pdf"
                  className="terms-pdf-iframe"
                  title="Terms & Conditions"
                >
                  <p>Your browser does not support PDFs. <a href="/assets/docs/TermsNConditions.pdf" target="_blank" rel="noopener noreferrer">Download the PDF</a> to view it.</p>
                </iframe>
              </div>
            `
          },
          {
            name: "community_guidelines",
            label: "I have read and understood the above Community Guidelines",
            type: "checkbox",
            required: true,
            order: 2,
            popupTitle: "Community Guidelines",
            popupContent: `
              <div className="terms-pdf-container">
                <p className="text-center mb-3">Please read the Community Guidelines document below. Scroll to the bottom to acknowledge.</p>
                <iframe
                  src="/assets/docs/EverythingPawzibleCommunityGuidelines.pdf"
                  className="terms-pdf-iframe"
                  title="Community Guidelines"
                >
                  <p>Your browser does not support PDFs. <a href="/assets/docs/EverythingPawzibleCommunityGuidelines.pdf" target="_blank" rel="noopener noreferrer">Download the PDF</a> to view it.</p>
                </iframe>
              </div>
            `
          },
          { name: "notifications_enabled", label: "Receive Notifications & Updates", type: "checkbox", required: false, order: 3 },
        ]
      }
    ]
  }
};

/**
 * Get form configuration by form type
 * @param {string} formType - The type of form (registration_pet_owner, registration_provider, contact_us)
 * @returns {Object} Form configuration object
 */
export const getFormConfig = (formType) => {
  return defaultFormConfigs[formType] || null;
};

/**
 * Get total steps for a form type
 * @param {string} formType - The type of form
 * @returns {number} Total number of steps
 */
export const getTotalSteps = (formType) => {
  const config = getFormConfig(formType);
  return config ? config.steps.length : 0;
};

/**
 * Get fields for a specific step
 * @param {string} formType - The type of form
 * @param {number} stepNumber - The step number
 * @returns {Array} Array of field configurations
 */
export const getFieldsForStep = (formType, stepNumber) => {
  const config = getFormConfig(formType);
  if (!config) return [];
  const step = config.steps.find(s => s.stepNumber === stepNumber);
  return step ? step.fields : [];
};

/**
 * Get step title
 * @param {string} formType - The type of form
 * @param {number} stepNumber - The step number
 * @returns {string} Step title
 */
export const getStepTitle = (formType, stepNumber) => {
  const config = getFormConfig(formType);
  if (!config) return '';
  const step = config.steps.find(s => s.stepNumber === stepNumber);
  return step ? step.stepTitle : '';
};

/**
 * Check if a field should be visible based on its condition
 * @param {Object} field - Field configuration
 * @param {Object} formData - Current form data
 * @returns {boolean} Whether the field should be visible
 */
export const isFieldVisible = (field, formData) => {
  if (!field.condition) return true;
  
  const { field: conditionField, value, conditionType = 'equals' } = field.condition;
  const fieldValue = formData[conditionField];
  
  if (conditionType === 'includes') {
    // For array values (checkboxes)
    if (Array.isArray(fieldValue)) {
      return fieldValue.includes(value);
    }
    return false;
  }
  
  // Default: equals comparison
  return fieldValue === value;
};

/**
 * Get dynamic options for a select field
 * @param {Object} field - Field configuration
 * @param {Object} formData - Current form data
 * @returns {Array} Array of options
 */
export const getDynamicOptions = (field, formData) => {
  if (!field.dynamicOptions) return field.options || [];
  
  const { field: dynamicField, options, defaultOption } = field.dynamicOptions;
  const fieldValue = formData[dynamicField];
  
  if (fieldValue && options[fieldValue]) {
    return options[fieldValue];
  }
  
  // Return default option or empty array
  return defaultOption ? [{ value: "", label: defaultOption }] : [];
};

/**
 * Validate a single field
 * @param {Object} field - Field configuration
 * @param {any} value - Field value
 * @returns {Object} Validation result { isValid, errorMessage }
 */
export const validateField = (field, value) => {
  if (field.required && (!value || (Array.isArray(value) && value.length === 0))) {
    return { isValid: false, errorMessage: `${field.label} is required` };
  }

  if (!value || (Array.isArray(value) && value.length === 0)) {
    return { isValid: true, errorMessage: null };
  }

  switch (field.type) {
    case 'email':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return { isValid: false, errorMessage: 'Please enter a valid email address' };
      }
      break;
    case 'tel':
      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(value.replace(/\s/g, ''))) {
        return { isValid: false, errorMessage: 'Please enter a valid 10-digit phone number' };
      }
      break;
    case 'password':
      if (value.length < 8) {
        return { isValid: false, errorMessage: 'Password must be at least 8 characters' };
      }
      break;
    case 'number':
      if (isNaN(value) || Number(value) < 0) {
        return { isValid: false, errorMessage: 'Please enter a valid number' };
      }
      break;
    default:
      break;
  }

  return { isValid: true, errorMessage: null };
};

/**
 * Validate all fields in a step
 * @param {Array} fields - Array of field configurations
 * @param {Object} formData - Current form data
 * @returns {Object} Validation result { isValid, errors }
 */
export const validateStep = (fields, formData) => {
  const errors = {};
  let isValid = true;

  fields.forEach(field => {
    // Skip validation for hidden conditional fields
    if (!isFieldVisible(field, formData)) return;
    
    const value = formData[field.name];
    const validation = validateField(field, value);
    if (!validation.isValid) {
      errors[field.name] = validation.errorMessage;
      isValid = false;
    }
  });

  return { isValid, errors };
};
